/**
 * BI dashboard grid layout — the single source of truth for the grid's column
 * count, shared by the frontend renderer and the backend widget-authoring
 * width cap so a widget's `width` means the same thing on both sides.
 *
 * ⚠️ CROSS-LANGUAGE SYNC: two places cannot import this constant and MUST be
 * kept in sync with it by hand:
 *   1. The AI widget-authoring tools are Python —
 *      `ai/agent_system/tools/domain/bi/_helpers.py` (`BI_GRID_COLUMNS`,
 *      `DEFAULT_WIDTH`, and the "1-N columns" tool-parameter docs) mirrors this.
 *   2. Tailwind's responsive grid class in `WidgetGrid.tsx`
 *      (`lg:grid-cols-N`) is a static string and must equal this value.
 *
 * Widths above this clamp to a full row, so the value also bounds the widget
 * `width` accepted by the authoring API (`create`/`update` widget).
 */
export const BI_GRID_COLUMNS = 4

/**
 * One grid row unit in px (`gridAutoRows`). A widget's `height` is a count of
 * these units. MUST equal `ROW_UNIT_PX` in `WidgetGrid.tsx` (that file imports
 * this constant so there is one source).
 */
export const BI_GRID_ROW_UNIT_PX = 112

/**
 * The grid's ROW gap in px (`WidgetGrid`'s `gap-4`). Load-bearing for the
 * derivation: a widget spanning N rows occupies N tracks PLUS the (N-1) gaps
 * BETWEEN them, so its real slot is `N*UNIT + (N-1)*GAP` — measured 240/368/
 * 624/1264 for spans 2/3/5/10. Sizing against `N*UNIT` alone under-counts the
 * slot and over-reserves a whole unit whenever the content lands in
 * `(N*UNIT, N*UNIT + (N-1)*GAP]`. `WidgetGrid` sets `rowGap` from this constant
 * so the renderer and the math cannot disagree.
 */
export const BI_GRID_ROW_GAP_PX = 16

/** Rendered height of a widget slot spanning `units` grid rows (tracks + the
 *  gaps between them). */
export function biGridSlotPx(units: number): number {
  const n = Math.max(1, Math.floor(units))
  return n * BI_GRID_ROW_UNIT_PX + (n - 1) * BI_GRID_ROW_GAP_PX
}

/**
 * Table render-height constants — these MIRROR the table widget's CSS (as of
 * STK-1672's table polish) and MUST be kept in sync with it (a divergence
 * silently mis-sizes derived tables, so `bi-layout.test.ts` pins the arithmetic
 * and this comment names the CSS owners):
 *   - `BI_TABLE_HEADER_PX` — the sticky header row: `TableHead` `h-8`
 *     (micro-caps) = 32px (`frontend/src/components/charts/DataTable.tsx`).
 *   - `BI_TABLE_ROW_PX` — one data row: the card `TableCell` `py-1.5` (12) +
 *     `text-sm` line (~20) + 1px border ≈ 33px. Uniform because the table is
 *     `whitespace-nowrap` (single-line rows, no data-dependent wrapping).
 *   - `BI_TABLE_CHROME_PX` — the card wrapper above the scroll box: the
 *     `WidgetCard` title band (`px-4 py-2`) + its `border-b` rule ≈ 39px. The
 *     table body is full-bleed (`CardContent` `p-0`), so it adds no padding.
 *   - `BI_TABLE_MAX_PX` — the scroll cap: past this the body scrolls (y only)
 *     instead of growing (`DataTable` `MAX_TABLE_PX`).
 */
export const BI_TABLE_HEADER_PX = 32
export const BI_TABLE_ROW_PX = 33
export const BI_TABLE_CHROME_PX = 39
export const BI_TABLE_MAX_PX = 1024

/**
 * The shortest slot a table may be rounded DOWN to (see `biTableHeightUnits`).
 * Rounding down buys back dead space at the cost of a few pixels of scroll —
 * a good trade for a tall table, a bad one for a short table, where hiding a
 * row behind a scrollbar to save space is worse than the space. At 1 unit
 * (112px) a table shows barely one row, so a table never rounds down to it: 2
 * is the floor. Raise this to make short tables show themselves more eagerly
 * (at the cost of their slack); it never affects a table that already fits.
 */
export const BI_TABLE_ROUND_DOWN_MIN_UNITS = 2

/**
 * Derive a TABLE widget's grid height (in row units) from its row count, so the
 * reserved slot ≈ the table's rendered content instead of an author-guessed
 * height. Deterministic: a pure function of `rowCount` and the mirrored CSS
 * constants above. Rows render at a fixed height (single-line, `nowrap`), so
 * `contentPx = header + rows·row + chrome` (verified against the real render:
 * a table measures `header + rows·row` to the sub-pixel).
 *
 * Pick the smallest N whose SLOT holds the content. The slot is
 * `biGridSlotPx(N)` — tracks AND the gaps between them — so:
 *     N*UNIT + (N-1)*GAP >= contentPx   ⇔   N >= (contentPx + GAP) / (UNIT + GAP)
 * Sizing against `N*UNIT` alone (ignoring the gap) silently over-reserves a
 * whole unit in the `(N*UNIT, N*UNIT + (N-1)*GAP]` band — e.g. a 5-row table
 * took 3 units (368px) where 2 (240px) fit with 4px to spare.
 *
 * Then round to the NEAREST slot, not always up. A step is ~3.9 table rows, so
 * always-up wastes nearly 4 empty rows whenever the content just clears a
 * boundary — that dead space, not the arithmetic, is what reads as broken. When
 * the smaller slot is the closer fit (content overshoots it by less than half a
 * step) we take it and let the body scroll those few pixels instead: the table
 * already scrolls past the cap, its header stays pinned, and a hair of scroll
 * beats ~4 rows of nothing. This bounds BOTH failure modes at half a step
 * (~1.9 rows) instead of one of them at a full step.
 *
 * `BI_TABLE_ROUND_DOWN_MIN_UNITS` is the floor: a SHORT table never rounds down.
 * Hiding one row of three behind a scrollbar to reclaim space is a bad trade —
 * a small table must simply show itself, and it keeps whatever slack that costs.
 *
 * Clamped to `[1, cap]`, the cap being the scroll-cap height past which the card
 * body scrolls internally. Applied at widget-creation time (the create endpoint
 * already ran the query, so the row count is known); NOT re-derived on refresh
 * (that stays out of scope).
 */
export function biTableHeightUnits(rowCount: number): number {
  const rows = Math.max(0, Math.floor(rowCount))
  const contentPx =
    BI_TABLE_HEADER_PX + rows * BI_TABLE_ROW_PX + BI_TABLE_CHROME_PX
  const step = BI_GRID_ROW_UNIT_PX + BI_GRID_ROW_GAP_PX
  const maxUnits = Math.ceil((BI_TABLE_MAX_PX + BI_GRID_ROW_GAP_PX) / step)
  const fits = Math.ceil((contentPx + BI_GRID_ROW_GAP_PX) / step)
  // The next slot down: it overshoots by `contentPx - biGridSlotPx(fits - 1)`,
  // which is in `(0, step]` by construction. Take it only when that overshoot is
  // the smaller error AND the result clears the short-table floor.
  const lower = fits - 1
  const overshoot = contentPx - biGridSlotPx(lower)
  const roundsDown =
    lower >= BI_TABLE_ROUND_DOWN_MIN_UNITS && overshoot * 2 <= step
  const units = roundsDown ? lower : fits
  return Math.min(Math.max(units, 1), maxUnits)
}
